import React, { useEffect, useState } from "react";
import { useLanguage } from '../../providers/preferenceProvider';
import '../../css/overlay.css';
import { createBooking, deleteBooking, updateBooking } from "../../api/booking-api";
import { useConfig } from "../../providers/configProvider";
import SearchableSelect from "./searchableSelect";
import { getUser, getUsers } from "../../api/user-api";
import { useToast } from "../../providers/toastProvider";

const EditBooking = ({
  isOpen,
  onClose,
  title,
  room,
  inputBooking,
  editable,
  newBooking
}) => {
  const { t } = useLanguage();
  const [booking, setBooking] = useState({
    title: '',
    description: '',
    attendees: 1,
  });
  const [notAttendingUsers, setNotAttendingUsers] = useState([]);
  const [search, setSearch] = useState('');
  const { config } = useConfig();
  const [clear, setClear] = useState(0);
  const { showActionToast, showToast, showLoading, hideLoading } = useToast();

  useEffect(() => {
    if (isOpen) {
      // Try input booking users, else config on new booking, else give up
      setNotAttendingUserOptions(inputBooking.Users ?? newBooking ? [{ id: config.user.id }] : [] );

      if (newBooking) {
        setCreateBooking()
      }
      else
      {
        setBooking(prev => ({
          ...prev,
          id: inputBooking.id,
          title: inputBooking.title || "Unknown Booking",
          description: inputBooking.description,
          attendees: inputBooking.attendees || 1,
          startTime: new Date(inputBooking.startTime),
          endTime: new Date(inputBooking.endTime),
          credits: inputBooking.credits || undefined,
          Users: inputBooking.Users
        }));
      }}

      
  }, [isOpen, room, inputBooking]
  );

  useEffect(() => {
    const timeout = setTimeout(() => setNotAttendingUserOptions(booking.Users || []), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  async function setCreateBooking() {
    const user = (await getUser(config.user.id))
    setBooking(prev => ({
      ...prev,
      title: 'Booking at ' + room.name || "Unknown Room",
      startTime: inputBooking.startTime,
      endTime: inputBooking.endTime,
      credits: inputBooking.credits || undefined,
      Users: [ { ...user.data.user, UserBooking: { ...user.data.user.UserBooking, creator: true } }]
    }));
  }

  async function setNotAttendingUserOptions(attendingUsers) {
    const params = new URLSearchParams();
    params.append('search', search);
    const allUsers = (await getUsers(params)).data.rows;
    setNotAttendingUsers(allUsers.filter(user => !attendingUsers.some(u => u.id === user.id)).map(user => { return { ...user, name: user.firstname + ' ' + user.lastname }}));
  }

  function toLocaleTimeString(date) {
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '00:00';
  }

  function addUser(user) {
    if (!booking.Users) {
      setBooking({ ...booking, Users: [{ user, ...{ new: true } }] });
      return;
    }
    
    if (booking.Users.find(u => u.id === user)) {
      return;
    }

    setBooking(prev => ({
      ...prev,
      Users: [...prev.Users, { ...(notAttendingUsers.find(u => user === u.id)), ...{ new: true } }]
    }));

    setClear(prev => prev + 1);
    setNotAttendingUsers(prev => prev.filter(u => u.id !== user));
  }

  function removeUser(user) {
    if (!booking.Users) {
      return;
    }

    setBooking(prev => ({
      ...prev,
      Users: prev.Users.filter(u => user.id !== u.id)
    }));

    setNotAttendingUsers(prev => [...prev, user]);
  }

  function confirmBooking() {
    showLoading();
    if ( newBooking ) {
      createBooking(
        config.user.id, 
        {
          title: booking.title?.trim(),
          roomId: room.id,
          startTime: new Date(booking.startTime).toISOString(),
          endTime: new Date(booking.endTime).toISOString(),
          description: booking.description,
          userList: booking.Users.map(user => user.id)
        }
      ).then(response => {
        showToast(t.toast.booking_success, 'success');
        closeOverlay();
        hideLoading();
      }).catch(error => {
        console.error("Error creating booking:", error);
        hideLoading();
      })
    }
    else {
      updateBooking({
        id: inputBooking.id,
        title: booking.title?.trim(),
        roomId: room.id,
        startTime: booking.startTime,
        endTime: booking.endTime,
        description: booking.description,
        userList: booking.Users.map(user => user.id)
      }).then(response => {
        showToast(t.booking + " " + t.toast.updated_success, 'success');
        closeOverlay();
        hideLoading();
      }).catch(error => {
        console.error("Error creating booking:", error);
        hideLoading();
      })
    }
  }

  function closeOverlay() {
    setBooking({
      title: '',
      description: '',
      attendees: 1,
    });
    onClose();
  }

  function trimLeadingZeros(s){
    return s.replace(/^0+(?=\d)/, '');
  }

  function deleteConfirmation() {
    console.log(inputBooking)
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', booking.title),
      handleDeleteBooking,
      null
    );
  }

  const handleDeleteBooking = () => {
    showLoading();
    deleteBooking(inputBooking.id).then(() => {
      closeOverlay()
      showToast(t.booking + " " + t.toast.deleted_success, 'success');
      hideLoading();
    }).catch(error => {
      console.error("Error deleting booking:", error);
      hideLoading();
    });
  }
    
  if (!isOpen) return null;

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={e => e.stopPropagation()}>
        <div className="overlay-header">
          <div>{title}</div>
        </div>

        <div className="book-overlay-room-info">
          <div className="overlay-details">
            <h2 className="overlay-roomname md:whitespace-nowrap">{room?.name}</h2>
            <p>{t.capacity}: {room.capacity}</p>
            <div className="overlay-image-container">
              <img src={room.image} alt={room.name} className="overlay-image" />
            </div>
          </div>

          <div className="overlay-user-book-info max-md:mt-1">
            <div className="overlay-form">
              <label>
                {t.title}
                <input
                  type="text"
                  disabled={!editable}
                  value={booking.title}
                  onChange={e => setBooking({ ...booking, title: e.target.value })}
                />
              </label>

              <p>{t.date}: {booking.startTime?.toLocaleDateString('nl-NL')}</p>
              <p>
                {t.time}: {trimLeadingZeros(toLocaleTimeString(booking.startTime))} -{' '}
                {trimLeadingZeros(toLocaleTimeString(booking.endTime))}
              </p>
              <p>
                Cost: {booking.credits} {booking.credits === 1 ? 'credit' : 'credits'}
              </p>

              <p>{t.description}:</p>
              <textarea
                disabled={!editable}
                className="overlay-admin-textarea"
                defaultValue={booking.description}
                onChange={e => setBooking({ ...booking, description: e.target.value })}
                rows={4}
              />

              <label>
                <div className="flex flex-row gap-2 items-center">
                  <div>{t.attendees}</div>
                  <div className="text-xs text-gray-600">
                    {booking.Users && `${booking.Users.length} / ${room.capacity}`}
                  </div>
                </div>

                <div className="overlay-user-list">
                  {booking?.Users &&
                    booking.Users.map((user, idx) => (
                      <div
                        key={idx}
                        className={`flex flex-row items-center justify-between border border-gray-300 rounded mb-0.5 gap-2 ${
                          user.new ? 'border border-red-400' : ''
                        }`}
                      >
                        <div className="flex flex-row items-center gap-2">
                          <img
                            src={user.image}
                            alt={user.firstname + ' ' + user.lastname}
                            className="w-[2rem] h-[2rem] rounded-full m-2"
                          />
                          <span>{user.firstname + ' ' + user.lastname}</span>
                        </div>

                        {!user?.UserBooking?.creator ? (
                          editable ? (
                            <div className="flex flex-row items-center gap-2">
                              {user.new ? <div>{t.new}!</div> : null}
                              <div
                                className="mr-2 text-sm btn border border-gray-400 px-2 py-1 rounded"
                                onClick={() => removeUser(user)}
                              >
                                {t.remove}
                              </div>
                            </div>
                          ) : null
                        ) : (
                          <div className="flex flex-row items-center gap-2">
                            {user.new ? <div>{t.new}!</div> : null}
                            <div className="mr-2 border border-gray-400 rounded px-2 py-1">
                              {t.creator}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {editable && (
                  <div className="flex gap-2">
                    <SearchableSelect
                      label=""
                      icon="share"
                      placeholder="Invite user"
                      options={notAttendingUsers}
                      onChange={addUser}
                      onSearch={setSearch}
                      clear={clear}
                    />
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>
        <div className="overlay-footer">
          {(!newBooking && editable) && (
            <div className="flex">
              <button className="btn" onClick={deleteConfirmation}>{`${t.delete} ${t.booking}`}</button>    
            </div>
          )}
          <div className="flex flex-row gap-4">
            <button className="btn-close" onClick={closeOverlay}>
              {t.close}
            </button>
            {editable ? (
              <button className={'btn ' + (editable ? '' : 'invisible')} onClick={confirmBooking}>
                {t.confirm}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBooking;